/**
 * LocationPicker — select a branch when a menu has multiple locations.
 *
 * Behavior:
 * - 0 locations → renders nothing (backward compatible)
 * - 1 location  → auto-selects, renders nothing
 * - 2+ locations → shows a picker sorted by distance (nearest first)
 */

import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapPin, Navigation, Search } from 'lucide-react'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { setSelectedLocationId } from '@/store/orderSlice'
import { useGeolocation } from '@/hooks/useGeolocation'
import { haversineDistance, formatDistance } from '@/utils/distance'
import { getStoreStatus } from '@/utils/storeHours'
import { getEffectiveSchedule } from '@/utils/effectiveSchedule'
import { slugifyLocationName } from '@/utils/slugifyLocationName'
import type { MenuLocation, RestaurantInfo } from '@/hooks/useMenuData'

interface LocationPickerProps {
  locations: MenuLocation[]
  restaurant: RestaurantInfo | null
  /** Hides the internal "Choose a location" heading (use when the parent Dialog already provides a title) */
  hideTitle?: boolean
  /** Called immediately after a location is selected (use to auto-close a parent Dialog) */
  onSelect?: () => void
}

/** Update the ?location= query param without a full navigation */
function syncLocationToUrl(loc: MenuLocation | null) {
  const url = new URL(window.location.href)
  if (loc) {
    url.searchParams.set('location', slugifyLocationName(loc.address ?? loc.location_name))
  } else {
    url.searchParams.delete('location')
  }
  window.history.replaceState(null, '', url.toString())
}

export function LocationPicker({ locations, restaurant, hideTitle, onSelect }: LocationPickerProps) {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const selectedId = useAppSelector((s) => s.order.selectedLocationId)

  // When no explicit selection exists, treat the first location as the effective selection.
  // This matches the useSelectedLocation fallback so the picker always reflects what the UI shows.
  const effectiveSelectedId = selectedId ?? (locations.length > 0 ? locations[0].id : null)
  const { coords: userCoords, isGranted } = useGeolocation()

  // Auto-select single location
  useEffect(() => {
    if (locations.length === 1 && selectedId !== locations[0].id) {
      dispatch(setSelectedLocationId(locations[0].id))
      syncLocationToUrl(locations[0])
    }
  }, [locations, selectedId, dispatch])

  // Sort by distance when geolocation is available
  const sorted = useMemo(() => {
    if (!userCoords || !isGranted) return locations

    return [...locations].sort((a, b) => {
      const distA =
        a.latitude != null && a.longitude != null
          ? haversineDistance(userCoords, { lat: a.latitude, lng: a.longitude })
          : Infinity
      const distB =
        b.latitude != null && b.longitude != null
          ? haversineDistance(userCoords, { lat: b.latitude, lng: b.longitude })
          : Infinity
      return distA - distB
    })
  }, [locations, userCoords, isGranted])

  // Search filter for large location lists
  const [searchQuery, setSearchQuery] = useState('')
  const showSearch = locations.length > 8

  const filtered = useMemo(() => {
    if (!searchQuery.trim()) return sorted
    const q = searchQuery.toLowerCase()
    return sorted.filter((loc) => {
      const name = loc.location_name?.toLowerCase() ?? ''
      const addr = loc.address?.toLowerCase() ?? ''
      const city = loc.city?.toLowerCase() ?? ''
      return name.includes(q) || addr.includes(q) || city.includes(q)
    })
  }, [sorted, searchQuery])

  // Nothing to render for 0 or 1 locations
  if (locations.length <= 1) return null

  return (
    <div className="space-y-2">
      {!hideTitle && <p className="text-sm font-semibold">{t('common:chooseALocation')}</p>}
      {showSearch && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder={t('common:searchLocations')}
            className="w-full h-10 pl-9 pr-3 rounded-xl border border-border bg-background text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-colors"
          />
        </div>
      )}
      <div className="space-y-2 max-h-[280px] overflow-y-auto pr-1">
        {filtered.length === 0 && (
          <p className="text-sm text-muted-foreground text-center py-4">
            {t('common:noLocationsMatch')}
          </p>
        )}
        {filtered.map((loc) => {
          const isSelected = effectiveSelectedId === loc.id
          const dist =
            isGranted && userCoords && loc.latitude != null && loc.longitude != null
              ? haversineDistance(userCoords, { lat: loc.latitude, lng: loc.longitude })
              : null

          const addressParts = [loc.address, loc.city, loc.state, loc.postal_code].filter(Boolean)
          const schedule = getEffectiveSchedule(loc, restaurant)
          const status = getStoreStatus(schedule.hours, {
            hoursExceptions: schedule.hoursExceptions,
            businessTimeZone: schedule.timeZone,
          })

          return (
            <button
              key={loc.id}
              type="button"
              onClick={() => {
                dispatch(setSelectedLocationId(loc.id))
                syncLocationToUrl(loc)
                onSelect?.()
              }}
              className={`w-full text-left flex items-start gap-3 rounded-xl border-2 px-4 py-3 transition-all active:scale-[0.98] cursor-pointer ${
                isSelected
                  ? 'border-primary bg-primary/5 shadow-sm'
                  : 'border-border hover:border-foreground/30 hover:bg-muted/50'
              }`}
            >
              <MapPin className={`h-4 w-4 mt-0.5 shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground'}`} />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-medium truncate">{loc.location_name}</p>
                  <span className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                    status.isOpen
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    <span className={`h-1.5 w-1.5 rounded-full ${status.isOpen ? 'bg-green-500' : 'bg-red-500'}`} />
                    {status.isOpen ? t('common:open') : t('common:closed')}
                  </span>
                </div>
                {addressParts.length > 0 && (
                  <p className="text-xs text-muted-foreground truncate">
                    {addressParts.join(', ')}
                  </p>
                )}
              </div>
              {dist != null && (
                <span className="flex items-center gap-1 text-xs text-muted-foreground shrink-0 mt-0.5">
                  <Navigation className="h-3 w-3" />
                  {formatDistance(dist)}
                </span>
              )}
            </button>
          )
        })}
      </div>
    </div>
  )
}
